import { Component, inject, signal, type OnDestroy, type OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { type SortDirection } from '@angular/material/sort'
import { DictionaryService, type DictionaryEntry } from '@core/locale/dictionary.service'
import { LocaleService } from '@core/locale/locale.service'
import { HttpService } from '@core/request/http.service'
import { SnackbarService } from '@core/snackbar/snackbar.service'
import { type Display } from '@core/type/display'
import { type numeric } from '@core/type/numeric'
import { type AppOption } from '@core/type/option'
import { type AppSort } from '@core/type/sort'
import { type THEME_TYPE } from '@core/type/theme'
import { IS_A_STRING_AND_NOT_EMPTY, IS_NUMERIC, IS_ON, IS_SET } from '@core/util/check.util'
import { OPTIONS_TO_KEY_VALUES, TO_ANY_ARRAY, TO_NUMBER } from '@core/util/convert.util'
import { FROM_ISO_TO_LOCAL_DTT } from '@core/util/display.util'
import { ENV_ACTV } from '@core/util/env.util'
import { GET_OBJECT_KEY_LIST } from '@core/util/object.util'
import { TO_STRING } from '@core/util/string.util'
import { environment } from '@env/environment'
import { DateTime } from 'luxon'
import { combineLatest } from 'rxjs'
import { filter, switchMap, takeWhile } from 'rxjs/operators'
import { DialogService, type DialogServiceConfirmParams } from './dialog.service'

@Component({
  selector: 'app-eq-al-dialog',
  templateUrl: './eq-al-dialog.component.html',
  styleUrls: ['./eq-al-dialog.component.scss'],
})
export class EqAlDialogComponent implements OnInit, OnDestroy {
  MAT_DIALOG_DATA = inject(MAT_DIALOG_DATA)
  MatDialogRef = inject(MatDialogRef)
  LocaleService = inject(LocaleService)
  DictionaryService = inject(DictionaryService)
  SnackbarService = inject(SnackbarService)
  FormBuilder = inject(FormBuilder)
  HttpService = inject(HttpService)
  DialogService = inject(DialogService)

  readonly bAliveWS = signal(false)

  readonly bACTV = environment.mode === 'ACTV'

  readonly eqId = TO_STRING(this.MAT_DIALOG_DATA?.eqId)

  readonly loNameWS = signal(TO_STRING(this.MAT_DIALOG_DATA?.loName))

  readonly eqIdentifier = TO_STRING(this.MAT_DIALOG_DATA?.eqIdentifier)

  readonly themeType: THEME_TYPE = 'primary'

  readonly alStatusActionIcon = {
    '-1': this.bACTV ? 'sticker-plus-outline' : 'send',
    0: 'alert-decagram-outline',
    1: 'checkbox-marked-circle-outline',
    2: 'package-down',
  } as const

  readonly alStatusActionClass = {
    '-1': this.bACTV ? 'al_action_ticket' : 'al_action_send',
    0: 'al_action_new',
    1: 'al_action_resolve',
    2: 'al_action_archive',
  } as const

  readonly alBgStatusActionClass = {
    '-1': this.bACTV ? 'al_bg_action_ticket' : 'al_bg_action_send',
    0: 'al_bg_action_new',
    1: 'al_bg_action_resolve',
    2: 'al_bg_action_archive',
  } as const

  readonly alStatusActionsOptions: AppOption[] = [
    { value: -1, label: this.bACTV ? 'ticket' : 'send' },
    { value: 0, label: 'reset' },
    { value: 1, label: 'resolve' },
    { value: 2, label: 'archive' },
  ] as const

  readonly alStatusActionsSelectorMultiple = this.alStatusActionsOptions

  readonly alStatusActionsSelectorSingle = this.alStatusActionsOptions

  readonly displayAlertStatusActions = OPTIONS_TO_KEY_VALUES(this.alStatusActionsOptions)

  readonly displayStatusOptions: AppOption[] = [
    { value: -1, label: this.bACTV ? 'ticket' : 'send' },
    { value: 0, label: 'in_progress' },
    { value: 1, label: 'resolved' },
    { value: 2, label: 'archived' },
  ] as const

  readonly alStatusSelector = this.displayStatusOptions.filter(o => o.value > -1)

  readonly displayStatus = OPTIONS_TO_KEY_VALUES(this.displayStatusOptions)

  readonly displayAlertStatusOptions: AppOption[] = [
    { value: -1, label: this.bACTV ? 'alertTicket' : 'alertSent' },
    { value: 0, label: 'alertIn_progress' },
    { value: 1, label: 'alertResolved' },
    { value: 2, label: 'alertArchived' },
  ] as const

  readonly displayAlertStatus = OPTIONS_TO_KEY_VALUES(this.displayAlertStatusOptions)

  readonly displayAlertStatusTheme = {
    '-1': 'primary',
    0: 'warn',
    1: 'accent',
    2: 'danger',
  } as const

  readonly displayLevelOptions: AppOption[] = [
    { value: 1, label: 'critical' },
    { value: 2, label: 'urgent' },
    { value: 3, label: 'informative' },
  ] as const

  readonly alLevelSelector = this.displayLevelOptions

  readonly displayLevel = OPTIONS_TO_KEY_VALUES(this.displayLevelOptions)

  readonly displayAlertLevelOptions: AppOption[] = [
    { value: 1, label: 'alertCritical' },
    { value: 2, label: 'alertUrgent' },
    { value: 3, label: 'alertInformative' },
  ] as const

  readonly displayAlertLevel = OPTIONS_TO_KEY_VALUES(this.displayAlertLevelOptions)

  readonly displayAlertLevelTheme = {
    1: 'danger',
    2: 'warn',
    3: 'primary',
  } as const

  readonly paginationForm = new FormGroup({
    page: new FormControl(1, { nonNullable: true }),
    perPage: new FormControl(200, { nonNullable: true }),
    pages: new FormControl(1, { nonNullable: true }),
  })

  readonly filteredCountWS = signal(NaN)

  readonly displayedColumnListWS = signal([
    'alStatus',
    // 'vehicle',
    // 'loName',
    // 'eoLogicalsn',
    // 'eqIdentifier',
    // 'esSerial',
    'atMinimumLevel',
    'alDate',
    'alResolved',
    'alDetails', // 'atDescription',
    'action',
  ])

  readonly defaultSortBy: AppSort[] = [
    { column: 'alStatus', order: 'asc' },
    { column: 'atMinimumLevel', order: 'asc' },
    { column: 'alDate', order: 'desc' },
  ] as const

  readonly sortByWS = signal<AppSort[]>(Array.from(this.defaultSortBy))

  readonly sortByColumnWS = signal(TO_STRING(this.defaultSortBy[0]?.column))

  readonly sortByOrderWS = signal<SortDirection>(this.defaultSortBy[0]?.order ?? '')

  readonly dataSourceWS = signal<any[]>([])

  readonly displayWS = signal<Display>(
    {
      cache: {
        alert: '',
        alerts: '',

        dialog_title: '',
        list_title: '',
        results_stats: '',
        results_page: '',

        page: '',
        pageFirst: '',
        pagePrevious: '',
        pageNext: '',
        per_page: '',
        overOf: '',

        status: '',
        level: '',
        date: '',
        duration: '',
        details: '',

        reset: '',
        ticket: '',
        send: '',
        resolve: '',
        archive: '',
        archive_all: '',
        auto_resolution: '',

        new: '',
        sent: '',
        resolved: '',
        archived: '',
        in_progress: '',

        alertNew: '',
        alertTicket: '',
        alertSent: '',
        alertResolved: '',
        alertArchived: '',
        alertIn_progress: '',

        alertCritical: '',
        alertUrgent: '',
        alertInformative: '',

        close: '',
        cancel: '',

        alerts_on_loName: '',
        alerts_on_the_same_terminal: '',
      },
      dictionary: {
        listTitleResultsPageResultsStats: (): Record<string, DictionaryEntry> => {
          const count = this.filteredCountWS()
          const bPlural = Math.abs(count) > 1
          const cD = this.LocaleService.localizeNumber({ value: count })
          const countD = IS_A_STRING_AND_NOT_EMPTY(cD) ? cD : '…'

          const page = TO_NUMBER(this.paginationForm.controls.page.value)
          const perPage = TO_NUMBER(this.paginationForm.controls.perPage.value)
          const pages = TO_NUMBER(this.paginationForm.controls.pages.value)

          const pageD = this.LocaleService.localizeNumber({ value: page })
          const pagesD = this.LocaleService.localizeNumber({ value: pages })

          const first = Math.min(((page - 1) * perPage) + 1, count)
          const last = Math.min((first + perPage) - 1, count)

          const fD = this.LocaleService.localizeNumber({ value: first })
          const firstD = IS_A_STRING_AND_NOT_EMPTY(fD) ? fD : '…'

          const lD = this.LocaleService.localizeNumber({ value: last })
          const lastD = IS_A_STRING_AND_NOT_EMPTY(lD) ? lD : '…'

          const displayCache = this.displayWS().cache

          const alert = displayCache.alert
          const alerts = displayCache.alerts
          const Page = displayCache.page
          const overOf = displayCache.overOf

          return {
            list_title: { default: `${countD} ${bPlural ? alerts : alert}` },
            results_page: { default: `${Page} ${pageD} / ${pagesD}` },
            results_stats: { default: `${firstD} - ${lastD} ${overOf} ${countD} ${bPlural ? alerts : alert}` },
          }
        },
        alerts_on_loName: {
          default: (): string => `Alerts on ${this.loNameWS()}`,
          fr: (): string => `Alertes sur ${this.loNameWS()}`,
          it: (): string => `Allarmi su ${this.loNameWS()}`,
        },
        alerts_on_the_same_terminal: {
          default: (): string => `Alerts on the same Equipment ${this.eqIdentifier}`,
          fr: (): string => `Alertes sur le même Équipement ${this.eqIdentifier}`,
          it: (): string => `Allarmi sullo stesso Apparato ${this.eqIdentifier}`,
        },
      },
      update: {
        all: () => {
          this.displayWS.update(d => {
            for (const k of GET_OBJECT_KEY_LIST(d.cache)) {
              const fromEntry = d.dictionary?.[k]

              d.cache[k] = this.DictionaryService.get(IS_SET(fromEntry) ? { fromEntry } : { k })
            }

            const dictionary = d.dictionary

            if (IS_SET(dictionary)) {
              const fromEntryRecord = dictionary.listTitleResultsPageResultsStats()

              for (const [k, fromEntry] of Object.entries(fromEntryRecord)) {
                d.cache[k] = this.DictionaryService.get({ fromEntry })
              }
            }

            return d
          })
        },
      },
    },
    { equal: () => false }
  )

  async ngOnInit (): Promise<void> {
    this.bAliveWS.set(true)

    combineLatest([this.LocaleService.localeBS, this.LocaleService.tzBS, this.DictionaryService.mLastRefreshBS]).pipe(
      takeWhile(this.bAliveWS),
      filter(([locale, tz, mLastRefresh]) => IS_A_STRING_AND_NOT_EMPTY(locale) && IS_A_STRING_AND_NOT_EMPTY(tz) && IS_SET(mLastRefresh)),
      switchMap(async () => {
        this.displayWS().update.all()

        await this.refreshAlList()
      })
    ).subscribe()
  }

  async refreshAlList (): Promise<void> {
    const eqIdList = [this.eqId]

    const q = { filtered: { eqIdList }, ordered: this.sortByWS, paginated: this.paginationForm.value }

    const qUri = encodeURIComponent(JSON.stringify(q))
    const { exception, result } = await this.HttpService.get({ path: `/al_fop_with_at_eo_eq_es_lo_ns_nb?q=${qUri}` })

    if (IS_SET(exception)) {
      this.SnackbarService.showDefaultServerError()
    }

    const rowList = TO_ANY_ARRAY(result?.rows)

    for (const row of rowList) {
      const alStatus = TO_STRING(row.alStatus)
      row.alStatusDisplay = TO_STRING(this.displayAlertStatus[alStatus] ?? alStatus)

      const atMinimumLevel = TO_STRING(row.atMinimumLevel)
      row.atMinimumLevelDisplay = TO_STRING(this.displayAlertLevel[atMinimumLevel])

      row.alDateDisplay = FROM_ISO_TO_LOCAL_DTT(row.alDate)

      row.alResolvedDisplay = this.DictionaryService.get({ k: 'alertResolved_on_dateTimeDisplay', params: row })

      const alDate = TO_STRING(row.alDate)
      const alResolved = TO_STRING(row.alResolved)
      const alDurationBounds = { start: alDate, ...(IS_A_STRING_AND_NOT_EMPTY(alResolved) ? { stop: alResolved } : {}) }
      const mStart = DateTime.fromISO(alDurationBounds.start)

      const boundStop = TO_STRING(alDurationBounds.stop)
      const mStop = IS_A_STRING_AND_NOT_EMPTY(boundStop) ? DateTime.fromISO(boundStop) : DateTime.now()

      const duration = mStart.diff(mStop)

      row.alDurationDisplay = this.DictionaryService.longEntries.durationDisplayParts({ duration, bAbsolute: true })[0]
    }

    this.dataSourceWS.set(rowList)

    const page = TO_NUMBER(result?.pagination?.page)

    if (IS_NUMERIC(page)) {
      this.paginationForm.patchValue({ page })
    }

    const perPage = TO_NUMBER(result?.pagination?.perPage)

    if (IS_NUMERIC(perPage)) {
      this.paginationForm.patchValue({ perPage })
    }

    const pages = TO_NUMBER(result?.pagination?.pages)

    if (IS_NUMERIC(pages)) {
      this.paginationForm.patchValue({ pages })
    }

    this.filteredCountWS.set(TO_NUMBER(result?.stats?.filteredCount))

    this.displayWS().update.all()
  }

  async jump (_: { to: string, page: number }): Promise<void> {
    if (_.to === 'al') {
      const pages = TO_NUMBER(this.paginationForm.value.pages)
      this.paginationForm.get('page')?.setValue(Math.max(1, Math.min(_.page, pages)))
      await this.refreshAlList()
    }
  }

  async jumpToAlPageFirst (): Promise<void> {
    await this.jump({ to: 'al', page: 1 })
  }

  async jumpToAlPagePrevious (): Promise<void> {
    await this.jump({ to: 'al', page: TO_NUMBER(this.paginationForm.value.page) - 1 })
  }

  async jumpToAlPageNext (): Promise<void> {
    await this.jump({ to: 'al', page: 1 + TO_NUMBER(this.paginationForm.value.page) })
  }

  async sort (_: { active: string, direction: SortDirection }): Promise<void> {
    this.sortByWS.set([{ column: _.active, order: _.direction }])
    this.sortByColumnWS.set(_.active)
    this.sortByOrderWS.set(_.direction)
    await this.jumpToAlPageFirst()
  }

  async handleAction (_: { action: numeric, row: any }): Promise<void> {
    const fromEntries = this.DictionaryService.extraDictionaries.alertStatusUpdateProcess

    const q = { alIdList: [_.row.alId] }

    const actionQuery = {
      '-1': async () => await this.HttpService.put({ path: this.bACTV ? '/al_ticket_bulk' : '/al_send_bulk', q }),
      0: async () => await this.HttpService.put({ path: '/al_reset_bulk', q }),
      1: async () => await this.HttpService.put({ path: '/al_resolve_bulk', q }),
      2: async () => await this.HttpService.put({ path: '/al_archive_bulk', q }),
    }

    const query = actionQuery[_.action]

    if (IS_SET(query)) {
      const { exception } = await query()

      if (IS_SET(exception)) {
        this.SnackbarService.showDefaultServerError()
      } else {
        const successMessages = {
          '-1': this.DictionaryService.get({ k: this.bACTV ? 'doneTicket' : 'doneSent', fromEntries }),
          0: this.DictionaryService.get({ k: 'doneReset', fromEntries }),
          1: this.DictionaryService.get({ k: 'doneResolve', fromEntries }),
          2: this.DictionaryService.get({ k: 'doneArchived', fromEntries }),
        }

        const successMessage = successMessages[_.action]

        if (IS_A_STRING_AND_NOT_EMPTY(successMessage)) {
          this.SnackbarService.showSuccess(successMessage)
        }

        if (TO_NUMBER(_.action) > -1) {
          _.row.alStatus = _.action
          _.row.alStatusDisplay = TO_STRING(this.displayAlertStatus[_.action] ?? _.action)
        }
      }
    }
  }

  async handleActionOnSelection (_: { action, rows?: any[] }): Promise<void> {
    const action = TO_NUMBER(_.action)

    const alIdList = TO_ANY_ARRAY(_.rows).filter(al => IS_NUMERIC(al.alId)).map(al => TO_STRING(al.alId))

    const n = alIdList.length

    const fromEntries = this.DictionaryService.extraDictionaries.alertStatusUpdateProcess

    const displayCache = this.displayWS().cache

    const optionsList: Record<numeric, DialogServiceConfirmParams> = {
      '-1': {
        title: this.DictionaryService.get({ k: ENV_ACTV ? 'titleTicket' : 'titleSend', params: { n }, fromEntries }),
        message: this.DictionaryService.get({ k: ENV_ACTV ? 'textTicket' : 'textSend', params: { n }, fromEntries }),
        cancelText: displayCache.cancel,
        confirmText: displayCache.send,
        themeType: 'primary',
      },
      0: {
        title: this.DictionaryService.get({ k: 'titleReset', params: { n }, fromEntries }),
        message: this.DictionaryService.get({ k: 'textReset', params: { n }, fromEntries }),
        cancelText: displayCache.cancel,
        confirmText: displayCache.reset,
        themeType: 'warning',
      },
      1: {
        title: this.DictionaryService.get({ k: 'titleResolve', params: { n }, fromEntries }),
        message: this.DictionaryService.get({ k: 'textResolve', params: { n }, fromEntries }),
        cancelText: displayCache.cancel,
        confirmText: displayCache.resolve,
        themeType: 'success',
      },
      2: {
        title: this.DictionaryService.get({ k: 'titleArchive', params: { n }, fromEntries }),
        message: this.DictionaryService.get({ k: 'textArchive', params: { n }, fromEntries }),
        cancelText: displayCache.cancel,
        confirmText: displayCache.archive,
        themeType: 'error',
      },
    }

    const options = optionsList[action]

    if ((n > 0) && IS_SET(options)) {
      const confirmed = await this.DialogService.confirm(options)

      if (IS_ON(confirmed?.bConfirmed)) {
        const actionPath = {
          '-1': ENV_ACTV ? '/al_ticket_bulk' : '/al_send_bulk',
          0: '/al_reset_bulk',
          1: '/al_resolve_bulk',
          2: '/al_archive_bulk',
        }

        const path = actionPath[action]

        if (IS_A_STRING_AND_NOT_EMPTY(path)) {
          const q = { alIdList }

          const { exception } = await this.HttpService.put({ path, q })

          if (IS_SET(exception)) {
            this.SnackbarService.showDefaultServerError()
          } else {
            const successMessages = {
              '-1': this.DictionaryService.get({ k: ENV_ACTV ? 'doneTicket' : 'doneSent', params: { n }, fromEntries }),
              0: this.DictionaryService.get({ k: 'doneReset', params: { n }, fromEntries }),
              1: this.DictionaryService.get({ k: 'doneResolve', params: { n }, fromEntries }),
              2: this.DictionaryService.get({ k: 'doneArchived', params: { n }, fromEntries }),
            }

            const successMessage = TO_STRING(successMessages[action])

            if (IS_A_STRING_AND_NOT_EMPTY(successMessage)) {
              this.SnackbarService.showSuccess(successMessage)
            }
          }
        }

        await this.refreshAlList()
      }
    }
  }

  ngOnDestroy (): void {
    this.bAliveWS.set(false)
  }
}
